import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from '@maximizer/core/shared/data-access';
import { Octopus, PersonalAccessToken } from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';

@Injectable()
export class PersonalAccessTokenService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  forKey(key?: string): Observable<PersonalAccessToken> {
    const criteria: Octopus.Criteria<PersonalAccessToken> = {
      SearchQuery: {
        Key: {
          $EQ: key,
        },
      },
    };

    return this.read(criteria);
  }

  forUser(key?: string): Observable<PersonalAccessToken> {
    const criteria: Octopus.Criteria<PersonalAccessToken> = {
      SearchQuery: {
        UserKey: {
          $EQ: key,
        },
      },
    };

    return this.read(criteria);
  }

  checkDuplicated(
    userKey: string,
    name: string,
  ): Observable<PersonalAccessToken> {
    const criteria: Octopus.Criteria<PersonalAccessToken> = {
      SearchQuery: {
        $AND: [
          {
            UserKey: {
              $EQ: userKey,
            },
          },
          {
            Name: {
              $EQ: name,
            },
          },
        ],
      },
    };

    return this.read(criteria);
  }

  read(
    criteria: Octopus.Criteria<PersonalAccessToken>,
  ): Observable<PersonalAccessToken> {
    const request: Octopus.PersonalAccessTokenReadRequest = {
      PersonalAccessToken: {
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            Database: 1,
            ExpirationDate: 1,
            RevokeDate: 1,
            Status: {
              Value: 1,
              DisplayValue: 1,
            },
          },
        },
        Criteria: criteria,
        OrderBy: {
          Fields: {
            ExpirationDate: 'DESC',
          },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.PersonalAccessTokenResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          const mapper = new Octopus.PersonalAccessTokenMapper();
          return mapper.from(result.PersonalAccessToken.Data);
        }),
      );
  }

  revoke(key: string): Observable<string> {
    const request: Octopus.PersonalAccessTokenModificationRequest = {
      PersonalAccessToken: {
        Data: {
          Key: key,
          Status: { Value: 2, DisplayValue: '' },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.PersonalAccessTokenModificationResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map(() => {
          return key;
        }),
      );
  }

  create(
    name: string,
    expiration: string,
    userKey: string,
  ): Observable<string | null> {
    const request: Octopus.PersonalAccessTokenModificationRequest = {
      PersonalAccessToken: {
        Data: {
          Key: null,
          Name: name,
          ExpirationDate: expiration,
          UserKey: { UID: userKey },
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.PersonalAccessTokenModificationResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          return result.PersonalAccessToken.Data?.Key;
        }),
      );
  }

  delete(key: string): Observable<string> {
    const request: Octopus.PersonalAccessTokenModificationRequest = {
      PersonalAccessToken: {
        Data: {
          Key: key,
        },
      },
      Globalization: Octopus.DefaultGlobalization,
    };

    return this.http
      .post<Octopus.PersonalAccessTokenModificationResponse>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map(() => {
          return key;
        }),
      );
  }
}
